export const SOV_IMPORT_TYPES = {
  BASE_CONTRACT: 'baseContract',
  SUB_ITEM: 'subItem',
  SUB_CONTRACT: 'subContract',
  SUB_CONTRACT_CHANGE: 'subContractChange',
  CHANGE: 'change',
  CUSTOM: 'custom',
};

export const SOV_IMPORT_TYPE_LABELS = {
  [SOV_IMPORT_TYPES.BASE_CONTRACT]: 'Base Contract',
  [SOV_IMPORT_TYPES.SUB_ITEM]: 'Sub-Item',
  [SOV_IMPORT_TYPES.SUB_CONTRACT]: 'Sub-Contract',
  [SOV_IMPORT_TYPES.SUB_CONTRACT_CHANGE]: 'Sub-Contract Change',
  [SOV_IMPORT_TYPES.CHANGE]: 'Change',
  [SOV_IMPORT_TYPES.CUSTOM]: 'Custom',
};

const SOV_IMPORT_TYPE_LIST = Object.values(SOV_IMPORT_TYPE_LABELS).join(', ');

// public s3 excel file
export const SOV_IMPORT_TEMPLATE_LINK = 'https://ontraccr-public.s3.us-west-2.amazonaws.com/Ontraccr+Contract+Import.xlsx';

const PERCENTAGE_HELP = 'Accepts 45%, 0.45 or 45. Leave blank on an unbilled row.';
const ONE_SOURCE_HELP = 'Only one of % Complete, Invoice or the quantities is needed. '
  + 'Anything else you fill in has to agree with it to the cent.';

/** These titles are the template's header row, so SheetHeaderMatcher maps every column of an
 * unedited template automatically. */
export const SOV_IMPORT_HEADERS = [
  {
    key: 'type',
    title: 'Type',
    required: true,
    helperText: `Must be one of the template's dropdown options: ${SOV_IMPORT_TYPE_LIST}.`,
  },
  {
    key: 'sectionName',
    title: 'Custom Section Name',
    required: false,
    helperText: 'Required on Custom rows and only allowed there. Rows sharing a name '
      + 'become one custom section.',
  },
  { key: 'description', title: 'Description', required: true },
  {
    key: 'contractAmount',
    title: 'Contract Amount',
    required: true,
    helperText: 'Accepts 1000.50 or $1,000.50. Enter a credit as a negative amount. '
      + 'Leave blank on a row with sub-items to take the sum of theirs.',
  },
  {
    key: 'percentageComplete',
    title: '% Complete',
    required: false,
    helperText: `${PERCENTAGE_HELP}\n\n${ONE_SOURCE_HELP}`,
  },
  {
    key: 'invoiceAmount',
    title: 'Invoice',
    required: false,
    helperText: `Pre-fills the first bill on this row.\n\n${ONE_SOURCE_HELP}`,
  },
  {
    key: 'notesColumn',
    title: 'Notes',
    required: false,
    helperText: 'Line breaks are kept.',
  },
  { key: 'unitType', title: 'Unit Type', required: false },
  {
    key: 'estimatedQuantity',
    title: 'Estimated Quantity',
    required: false,
    helperText: ONE_SOURCE_HELP,
  },
  {
    key: 'billableQuantity',
    title: 'Billable Quantity',
    required: false,
    helperText: ONE_SOURCE_HELP,
  },
  {
    key: 'completedQuantity',
    title: 'Completed Quantity',
    required: false,
    helperText: 'Production quantity. Leave blank on a row with sub-items.',
  },
  { key: 'billingCode', title: 'Billing Code', required: false },
  {
    key: 'number',
    title: 'Number',
    required: false,
    helperText: 'Only allowed on Change and Sub-Contract Change rows.',
  },
];

export const SOV_IMPORT_REQUIRED_KEYS = SOV_IMPORT_HEADERS
  .filter(({ required }) => required)
  .map(({ key }) => key);

export const SOV_IMPORT_GUIDELINES = [
  'One row per contract line, in the order you want it to appear. Rows are numbered per table in the order they are listed.',
  `Type must be one of the dropdown options: ${SOV_IMPORT_TYPE_LIST}.`,
  'A Sub-Item must sit directly under the Base Contract, Custom or Sub-Item row it belongs to. It cannot be the first row.',
  'A Sub-Contract Change must sit directly under the Sub-Contract row it belongs to, or under another Sub-Contract Change of that sub-contract.',
  'A row with Sub-Items takes its Contract Amount from them, so leave it blank or make it match their sum exactly.',
  'A row with Sub-Items must leave % Complete, Invoice, Estimated Quantity, Billable Quantity and Completed Quantity blank. Those come from the sub-items.',
  'Custom Section Name is required on Custom rows and must be blank on every other type. Rows sharing a name become one custom section.',
  'Number is only for Change and Sub-Contract Change rows and must be blank on every other type.',
  'Contract Amount and Invoice can be entered as 1000.50 or $1,000.50. Enter a credit (money taken off the contract) as a negative amount, such as -500.',
  '% Complete accepts 45%, 0.45 or 45, and cannot be more than 100%.',
  'Progress has one source per row: the quantities first, then % Complete, then Invoice. If you fill in more than one they have to agree to the cent, otherwise the row is rejected.',
  'Notes keep their line breaks. Blank rows are skipped.',
  'Every row has to be valid. If any row has an error nothing is imported.',
  'Import is only available while the contract is empty, so run it before adding rows by hand.',
];
